import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Badge } from './Badge';
import type { BadgeTone } from './Badge';

export type ToastTone = 'info' | 'ok' | 'caution' | 'warning' | 'critical';

export interface ToastItem {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** 0 表示不自动关闭 */
  timeoutMs: number;
}

export interface ToastInput {
  tone: ToastTone;
  title: string;
  description?: string;
  timeoutMs?: number;
}

export interface ToastApi {
  show: (input: ToastInput) => void;
  info: (title: string, description?: string) => void;
  ok: (title: string, description?: string) => void;
  caution: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  critical: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONES: Record<ToastTone, BadgeTone> = {
  info: 'info',
  ok: 'ok',
  caution: 'caution',
  warning: 'warning',
  critical: 'critical',
};

const LABELS: Record<ToastTone, string> = {
  info: '提示',
  ok: '成功',
  caution: '注意',
  warning: '警告',
  critical: '严重',
};

/**
 * 全局提示条。放在页面右下角，不拦截指针事件（只有关闭按钮接收点击），
 * 因此不会挡住下方的操作按钮。
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const api = useMemo<ToastApi>(() => {
    const show = ({ tone, title, description, timeoutMs }: ToastInput) => {
      seq.current += 1;
      const id = `toast_${seq.current}`;
      const item: ToastItem = {
        id,
        tone,
        title,
        description,
        timeoutMs: timeoutMs ?? (tone === 'critical' || tone === 'warning' ? 8000 : 4000),
      };
      setItems((current) => [...current.slice(-3), item]);
    };

    return {
      show,
      info: (title, description) => show({ tone: 'info', title, description }),
      ok: (title, description) => show({ tone: 'ok', title, description }),
      caution: (title, description) => show({ tone: 'caution', title, description }),
      warning: (title, description) => show({ tone: 'warning', title, description }),
      critical: (title, description) => show({ tone: 'critical', title, description }),
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {items.length > 0 ? (
        <div className="pointer-events-none fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[1150] flex w-[min(20rem,calc(100vw-2rem))] flex-col items-end gap-2">
          {items.map((item) => (
            <ToastCard key={item.id} item={item} onDismiss={dismiss} />
          ))}
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: (id: string) => void }) {
  useEffect(() => {
    if (item.timeoutMs <= 0) return;
    const timer = window.setTimeout(() => onDismiss(item.id), item.timeoutMs);
    return () => window.clearTimeout(timer);
  }, [item.id, item.timeoutMs, onDismiss]);

  return (
    <div
      role="status"
      className={[
        'pointer-events-none w-full rounded-lg border bg-surface-1 px-3.5 py-3',
        item.tone === 'critical'
          ? 'border-critical/70'
          : item.tone === 'warning'
            ? 'border-warning/60'
            : 'border-line-strong',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge tone={TONES[item.tone]} solid={item.tone === 'critical' || item.tone === 'warning'}>
            {LABELS[item.tone]}
          </Badge>
          <p className="text-sm font-medium text-fg">{item.title}</p>
        </div>
        <button
          type="button"
          aria-label="关闭提示"
          onClick={() => onDismiss(item.id)}
          className="pointer-events-auto -mt-0.5 -mr-1 rounded px-1 text-xs text-fg-subtle hover:text-fg"
        >
          ✕
        </button>
      </div>
      {item.description ? (
        <p className="mt-1 text-xs leading-relaxed text-fg-muted">{item.description}</p>
      ) : null}
    </div>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast 必须在 ToastProvider 内使用');
  return context;
}
